import { deviceDescription } from '@api/devices/identity-contracts';
import type { AuditCategory, AuditTrailEvent } from '@api/devices/contracts';
import { ScrollText, X } from 'lucide-react';
import { Dialog } from 'radix-ui';
import { Link, useSearchParams } from 'react-router';
import { absolute, ago } from '../lib/time';
import { auditActor, auditLabel, auditTitle } from '../operations/audit';
import { auditDetailFields, auditTarget, recordedText, recordedTime } from '../operations/audit-details';
import { useScope } from '../scope/scope';
import { useAuditTrail } from '../search/hooks';
import { useSession } from '../session/session';
import { Button } from '../ui/Button';
import { cn } from '../ui/cn';
import { ErrorPanel } from '../ui/errors';
import { Card, EmptyState, Skeleton, useNow } from '../ui/misc';
import { PageHeader } from './PageHeader';

const TABS: Array<[AuditCategory | 'all', string]> = [['all', 'Everything'], ['access', 'Sign-ins and keys'], ['devices', 'Devices'], ['jobs', 'Jobs'], ['remote', 'Remote sessions']];
const isCategory = (value: string | null): value is AuditCategory => value === 'access' || value === 'devices' || value === 'jobs' || value === 'remote';

/** The workspace's append-only trail: who did what, when, and to which device. */
export function AuditPage() {
  const { mspId } = useScope();
  const { session } = useSession();
  const [params, setParams] = useSearchParams();
  const raw = params.get('category');
  const category = isCategory(raw) ? raw : undefined;
  const trail = useAuditTrail(category);
  const now = useNow();
  const wide = session.memberships.some(m => m.mspId === mspId && m.organizationId === null);
  const events = trail.data?.pages.flatMap(p => p.events) ?? [];
  const choose = (key: AuditCategory | 'all') => setParams(current => {
    const next = new URLSearchParams(current);
    if (key === 'all') next.delete('category'); else next.set('category', key);
    return next;
  }, { replace: true });
  return (
    <div className="space-y-6">
      <PageHeader title="Audit" description="Sign-ins, security keys, enrollment and every job in this workspace, newest first. Entries are only ever added, never edited." />
      {!wide && (
        <p className="text-[13px] text-ink-2">You see events for the devices and jobs you can access. Sign-ins and security keys are shown to members with access to the whole workspace.</p>
      )}
      <div role="tablist" aria-label="Event type" className="flex gap-1 overflow-x-auto border-b border-line [scrollbar-width:none]">
        {TABS.map(([key, label]) => {
          const active = (category ?? 'all') === key;
          return (
            <button key={key} role="tab" type="button" aria-selected={active} onClick={() => choose(key)}
              className={cn('-mb-px whitespace-nowrap border-b-2 px-3 py-2 text-[13.5px] font-medium transition-colors',
                active ? 'border-brand text-ink' : 'border-transparent text-ink-3 hover:text-ink')}>
              {label}
            </button>
          );
        })}
      </div>
      {trail.isPending ? <Card className="space-y-3 p-4">{[0, 1, 2, 3].map(i => <Skeleton key={i} className="h-10 w-full" />)}</Card>
        : trail.isError && !events.length ? <Card><ErrorPanel error={trail.error} onRetry={() => void trail.refetch()} /></Card>
        : !events.length ? (
          <Card><EmptyState icon={<ScrollText className="size-5" />} title="Nothing recorded here yet">
            {category ? 'Nothing of this type has happened in this workspace that you can see.'
              : wide ? 'Sign-ins, enrollment and jobs will be recorded here as they happen.' : 'Jobs and enrollment on devices you can access will be recorded here as they happen.'}
          </EmptyState></Card>
        ) : (
          <Card className="overflow-hidden">
            {trail.isRefetchError && (
              <p role="status" className="border-b border-line px-4 py-2 text-[12.5px] text-attention">
                Couldn’t refresh; showing events as of the last successful load. <button type="button" className="font-medium underline" onClick={() => void trail.refetch()}>Try again</button>
              </p>
            )}
            <ol key={`${mspId}:${category ?? 'all'}`} aria-label="Audit events" className="divide-y divide-line">
              {events.map(e => <AuditRow key={e.id} event={e} now={now} mspId={mspId} />)}
            </ol>
            {trail.isFetchNextPageError && <ErrorPanel className="border-t border-line" error={trail.error} onRetry={() => void trail.fetchNextPage()} />}
            {trail.hasNextPage && !trail.isFetchNextPageError && (
              <div className="border-t border-line p-3 text-center">
                <Button size="sm" disabled={trail.isFetchingNextPage} onClick={() => void trail.fetchNextPage()}>
                  {trail.isFetchingNextPage ? 'Loading…' : 'Show older events'}
                </Button>
              </div>
            )}
          </Card>
        )}
    </div>
  );
}

function AuditRow({ event: e, now, mspId }: { event: AuditTrailEvent; now: number; mspId: string }) {
  // Event details are stored data: shown as text, never interpreted.
  const result = e.action === 'job.finished' && typeof e.details.status === 'string' ? e.details.status : null;
  const note = e.action === 'job.rejected' && typeof e.details.note === 'string' && e.details.note ? e.details.note : null;
  return (
    <Dialog.Root>
    <li className="flex flex-wrap items-baseline gap-x-3 gap-y-1 px-4 py-3">
      <div className="min-w-0 flex-1">
        <Dialog.Trigger asChild>
          <button type="button" className="min-h-11 max-w-full rounded-md text-left font-medium [overflow-wrap:anywhere] hover:underline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand">{recordedText(auditTitle(e))}</button>
        </Dialog.Trigger>
        {result && <span className="text-ink-2"> · {recordedText(result)}</span>}
        <div className="text-[12.5px] text-ink-3">
          <span>{auditActor(e)}</span>
          {e.device && <> · <Link to={`/w/${mspId}/devices/${e.device.id}`} className="font-medium text-ink-2 hover:text-ink hover:underline">{deviceDescription(e.device)}</Link></>}
        </div>
        {note && <p className="mt-1 break-words text-[12.5px] text-ink-2">“{recordedText(note)}”</p>}
      </div>
      <time dateTime={e.at} title={absolute(e.at)} className="tabular whitespace-nowrap text-[12.5px] text-ink-3">{ago(e.at, now)}</time>
    </li>
    <AuditDetail event={e} mspId={mspId} />
    </Dialog.Root>
  );
}

/** The already returned row is the entire data source; opening never fetches anything. */
function AuditDetail({ event: e, mspId }: { event: AuditTrailEvent; mspId: string }) {
  const target = auditTarget(e, mspId);
  const deviceTarget = e.device ? auditTarget({ ...e, targetType: 'device', targetId: e.device.id }, mspId) : null;
  const fields = [
    { label: 'Event ID', value: String(e.id) },
    { label: 'Action', value: recordedText(e.action) },
    { label: 'Recorded at', value: recordedTime(e.at) },
    { label: 'Actor type', value: recordedText(e.actorType) },
    { label: 'Actor identifier', value: recordedText(e.actorId) },
    { label: 'Target type', value: recordedText(e.targetType) },
  ];
  return <Dialog.Portal>
    <Dialog.Overlay className="fixed inset-0 z-50 bg-black/40 animate-fade" />
    <Dialog.Content className="fixed inset-x-0 bottom-0 z-50 max-h-[90dvh] overflow-y-auto rounded-t-2xl border border-line bg-raised p-5 shadow-pop sm:inset-auto sm:left-1/2 sm:top-[5dvh] sm:w-[min(600px,calc(100vw-2rem))] sm:-translate-x-1/2 sm:rounded-2xl">
      <div className="flex items-start gap-3">
        <div className="min-w-0 flex-1">
          <Dialog.Title className="text-[17px] font-semibold">Audit event</Dialog.Title>
          <Dialog.Description className="mt-1 text-[13px] text-ink-2 [overflow-wrap:anywhere]">
            {Object.hasOwn(auditLabel, e.action) ? auditLabel[e.action] : 'Recorded event'}. Read-only recorded information.
          </Dialog.Description>
        </div>
        <Dialog.Close className="flex size-11 shrink-0 items-center justify-center rounded-lg text-ink-3 hover:bg-hover focus-visible:outline-2 focus-visible:outline-brand" aria-label="Close event details"><X className="size-5" /></Dialog.Close>
      </div>
      <dl className="mt-4 space-y-3 text-[13px]">
        {fields.map(f => <DetailField key={f.label} {...f} />)}
        <div><dt className="text-ink-3">Target identifier</dt><dd className="mt-0.5 whitespace-pre-wrap [overflow-wrap:anywhere]">{target.href
          ? <Link className="inline-block min-h-11 py-2 font-medium underline" to={target.href}>{target.label}</Link> : target.label}</dd></div>
        <div><dt className="text-ink-3">Device</dt><dd className="mt-0.5 [overflow-wrap:anywhere]">{e.device && deviceTarget
          ? deviceTarget.href ? <Link className="inline-block min-h-11 py-2 font-medium underline" to={deviceTarget.href}>{recordedText(deviceDescription(e.device))} · {e.device.id.slice(0, 8)}</Link>
            : `${recordedText(deviceDescription(e.device))} · ${deviceTarget.label}`
          : 'Not recorded'}</dd></div>
        {auditDetailFields(e).map(f => <DetailField key={f.label} {...f} />)}
      </dl>
    </Dialog.Content>
  </Dialog.Portal>;
}
function DetailField({ label, value }: { label: string; value: string }) {
  return <div><dt className="text-ink-3">{label}</dt><dd className="mt-0.5 whitespace-pre-wrap [overflow-wrap:anywhere]">{value}</dd></div>;
}
